function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Code for mobile-drawer menu starts here 
const trapFocusHandlers = {};

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function setSidePanelInteractivity(panel, isOpen) {
  if (!panel) return;

  if (isOpen) {
    panel.removeAttribute('inert');
    panel.removeAttribute('aria-hidden');
    return;
  }

  panel.setAttribute('inert', '');
  panel.setAttribute('aria-hidden', 'true');
}
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      document.body.classList.add('overflow-hidden')
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    document.body.classList.remove('overflow-hidden')
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    if(this.header){
      this.header.classList.remove('menu-open');
    }
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

}

customElements.define('header-drawer', HeaderDrawer);

/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer {

  constructor() {
    this.container = document.getElementById('Cart-Drawer');
    this.backToTopElement = document.querySelector("back-to-top")
    if (!this.container) {
      return;
    }
    this.buttons = document.querySelectorAll('#cart-drawer-toggle');

    if (!this.buttons.length) {
      return;
    }
    this.bindToggleButtons();

    if (this.container.dataset.cartDrawerInitialized === 'true') {
      this.notesToggle();
      this.termsCheckbox();
      return;
    }

    this.container.dataset.cartDrawerInitialized = 'true';

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.container.addEventListener('change', this.debouncedOnChange.bind(this));
    this.container.addEventListener('click', this.handleRemoveClick.bind(this));

    this.notesToggle();
    // Terms checkbox
    this.termsCheckbox();
  }
  bindToggleButtons() {
    this.buttons.forEach((button) => {
      if (button.dataset.cartDrawerBound === 'true') {
        return;
      }
      button.dataset.cartDrawerBound = 'true';
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }
  open() {
    document.body.classList.add('open-cc');
    document.body.style.overflow = "hidden";
    setSidePanelInteractivity(this.container, true);
    this.container.classList.add('active');
    this.container.focus();
    if (this.backToTopElement) this.backToTopElement.style.position = "relative";

    dispatchCustomEvent('cart-drawer:open');
  }
  onChange(event) {
    if (event.target.classList.contains('qty')) {
      this.updateQuantity(event.target.dataset.index, event.target.value);
    }
  }

  handleRemoveClick(event) {
    const removeButton = event.target.closest('.remove');
    if (removeButton && removeButton.dataset.index) {
      event.preventDefault();
      if (this.container.querySelector(`#CartDrawerItem-${removeButton.dataset.index}.thb-loading`)) {
        return;
      }
      this.updateQuantity(removeButton.dataset.index, '0');
    }
  }

  removeProductEvent() {
    // Kept for backward compatibility if called elsewhere
  }
  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }];
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
  termsCheckbox() {
    let terms_checkbox = this.container.querySelector('#CartDrawerTerms'),
      checkout_button = this.container.querySelector('.button.checkout');

    if (terms_checkbox && checkout_button) {
      terms_checkbox.setCustomValidity(theme.strings.requiresTerms);
      if (checkout_button.dataset.cartTermsBound === 'true') {
        return;
      }

      checkout_button.dataset.cartTermsBound = 'true';
      checkout_button.addEventListener('click', function (e) {
        if (!terms_checkbox.checked) {
          terms_checkbox.reportValidity();
          terms_checkbox.focus();
          e.preventDefault();
        }
      });
    }
  }
  notesToggle() {
    let notes_toggle = document.getElementById('order-note-toggle');

    if (!notes_toggle) {
      return;
    }

    if (notes_toggle.dataset.cartNotesBound === 'true') {
      return;
    }

    // Guard against missing sibling element (e.g. after cart HTML is re-rendered)
    const sibling = notes_toggle.nextElementSibling;
    if (!sibling) {
      return;
    }

    notes_toggle.dataset.cartNotesBound = 'true';
    notes_toggle.addEventListener('click', (event) => {
      sibling.classList.add('active');
    });
    sibling.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        sibling.classList.remove('active');
        this.saveNotes();
      });
    });
  }
  saveNotes() {
    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body: JSON.stringify({
        'note': document.getElementById('mini-cart__notes').value
      })
    });
  }
  updateQuantity(line, quantity) {
    const cartDrawerItem = this.container.querySelector(`#CartDrawerItem-${line}`);
    if (cartDrawerItem && cartDrawerItem.classList.contains('thb-loading')) {
      return;
    }

    if (cartDrawerItem) {
      cartDrawerItem.classList.add('thb-loading');
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    dispatchCustomEvent('line-item:change:start', {
      quantity: quantity
    });
    fetch(`${theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      ...{
        body
      }
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);


        this.getSectionsToRender().forEach((section => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement || !parsedState.sections || !parsedState.sections[section.section]) {
            return;
          }

          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;

          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        }));

        this.notesToggle();
        this.termsCheckbox();

        dispatchCustomEvent('line-item:change:end', {
          quantity: quantity,
          cart: parsedState
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        const loadingItem = this.container.querySelector(`#CartDrawerItem-${line}`);
        if (loadingItem) {
          loadingItem.classList.remove('thb-loading');
        }
      });
  }
}

document.addEventListener("DOMContentLoaded" , () => {
  if (typeof CartDrawer !== 'undefined') {
    new CartDrawer();
  }
})

// Lazy sections Implementation
class LazySection extends HTMLElement {
  static sectionsToFetch = [];
  static abortController = null;

  constructor() {
    super();

    this.section = this.closest('section')?.id || this.closest('[id]').id;
    this.section = this.section?.replace('shopify-section-', '');

    this.trigger = this.dataset.triggerEvent || 'intersection-observer';
    this.targetSelector = this.dataset.triggerTarget || 'body';

    this.boundHandleMouseover = this.handleMouseover.bind(this);
    this.boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    const targetElement = document.querySelector(this.targetSelector);

    if (this.trigger === 'mouseover') {
      if (screen.width < 990) return;

      targetElement.addEventListener('mouseover', this.boundHandleMouseover);
    } else if (this.trigger === 'click') {
      targetElement.addEventListener('click', this.boundHandleClick);
    } else {
      // Default to intersection observer
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: '0px 0px 0px 0px' });
      this.observer.observe(this);
    }
  }

  // Unobserve when the element is removed from the DOM
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Handler for intersection-observer trigger
  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    
    this.fetchSections();
  }

  handleMouseover() {
    this.removeTrigger();
    this.fetchSections();
  }

  handleClick() {
    this.removeTrigger();
    this.fetchSections();
  }

  fetchSections() {
    LazySection.sectionsToFetch.push(this.section);
    const sectionsToFetchBatch = LazySection.sectionsToFetch.length == 5 ? LazySection.sectionsToFetch.splice(0, 5) : LazySection.sectionsToFetch;

    // Check if there is an ongoing network request
    if (LazySection.abortController) {
      LazySection.abortController.abort('New fetch initiated');
    }

    LazySection.abortController = sectionsToFetchBatch.length < 5 && new AbortController();
    fetch(window.location.pathname + '?sections=' + sectionsToFetchBatch.join(','), LazySection.abortController.signal ? { signal: LazySection.abortController.signal } : {})
      .then((response) => response.json())
      .then((json) => {
        for (const [key, value] of Object.entries(json)) {
          const sectionContent = new DOMParser().parseFromString(value, 'text/html').getElementById('shopify-section-' + key);

          if (sectionContent && sectionContent.innerHTML.trim().length) {
            const section = document.getElementById('shopify-section-' + key);
            section.innerHTML = sectionContent.innerHTML;

            section.querySelectorAll('script').forEach((oldScriptTag) => {
              const newScriptTag = document.createElement('script');
              Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value);
              });
              newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
              oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
            });
          }
        }

        if (sectionsToFetchBatch.length < 5) LazySection.sectionsToFetch = [];
      })
      .catch((e) => {
        console.warn(e);
      });
  }

  removeTrigger() {
    const targetElement = document.querySelector(this.targetSelector);

    if (targetElement) {
      targetElement.removeEventListener('mouseover', this.boundHandleMouseover);
      targetElement.removeEventListener('click', this.boundHandleClick);
    }
  }
}

customElements.define('lazy-section', LazySection);

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');
      this.drawer = document.getElementById('Product-Drawer');
      this.body = document.body;

      this.addEventListener('click', this.setupEventListener.bind(this));
      this.cc.addEventListener('click', this.setupCCEventListener.bind(this));
      this.backToTopElement = document.querySelector("back-to-top")

    }
    setupCCEventListener(e) {
      this.body.classList.remove('open-cc');
      this.body.style.overflow = ""
      this.drawer.classList.remove('active');
      this.drawer.querySelector('#Product-Drawer-Content').innerHTML = '';
    }
    setupEventListener(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle,
        href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;

      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');
      if (!href || !productHandle) {
        return;
      }
      if (this.classList.contains('loading')) {
        return;
      }
      this.classList.add('loading');
      fetch(href, {
        method: 'GET'
      })
        .then((response) => {
          this.classList.remove('loading');
          return response.text();
        })
        .then(text => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content').innerHTML;

          this.renderQuickview(sectionInnerHTML, href, productHandle);

        });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (sectionInnerHTML) {

        this.drawer.querySelector('#Product-Drawer-Content').innerHTML = sectionInnerHTML;

        let js_files = this.drawer.querySelector('#Product-Drawer-Content').querySelectorAll('script');

        if (js_files.length > 0) {
          var head = document.getElementsByTagName('head')[0];
          js_files.forEach((js_file, i) => {
            let script = document.createElement('script');
            script.src = js_file.src;
            head.appendChild(script);
          });
        }

        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        }, 300);



        this.body.classList.add('open-cc');
        setSidePanelInteractivity(this.drawer, true);
        this.drawer.classList.add('active');
        this.drawer.querySelector('.side-panel-close').focus();
        this.body.style.overflow = 'hidden'
        if (this.backToTopElement) this.backToTopElement.style.position = "relative"
        dispatchCustomEvent('quick-view:open', {
          productUrl: href,
          productHandle: productHandle
        });
      }
    }
  }
  customElements.define('quick-view', QuickView);
}


if (!customElements.get("side-panel-close")) {
class PanelClose extends HTMLElement {

  constructor() {
    super();
    let cc = document.querySelector('.click-capture');
    this.backToTopElement = document.querySelector("back-to-top")

    // Add functionality to buttons
    this.addEventListener('click', (e) => this.close_panel(e));
    document.addEventListener('keyup', (e) => {
      if (e.code) {
        if (e.code.toUpperCase() === 'ESCAPE') {
          document.querySelectorAll('.side-panel').forEach((panel) => {
            panel.classList.remove('active');
            setSidePanelInteractivity(panel, false);
            document.body.classList.remove('open-cc');
          });
        }
      }
    });
    cc.addEventListener('click', (e) => {
      document.body.classList.remove('open-cc');
      document.body.style.overflow = ''
      document.body.classList.remove("overflow-hidden")
      if (this.backToTopElement) this.backToTopElement.style.position = ""
      if (document.querySelector('.side-panel.active')) {
        const activePanel = document.querySelector('.side-panel.active');
        activePanel.classList.remove('active');
        setSidePanelInteractivity(activePanel, false);
        document.body.style.overflow = '';
      }
    });
  }

  close_panel(e) {
    e.preventDefault();
    document.body.style.overflow = ''
    let panel = e.target.closest('.side-panel');
    panel.classList.remove('active');
    setSidePanelInteractivity(panel, false);
    document.body.classList.remove('open-cc');
    document.body.style.overflow="scroll";
    document.body.classList.remove("overflow-hidden")
    if (this.backToTopElement) this.backToTopElement.style.position = ""

  }
}
customElements.define('side-panel-close', PanelClose);
}

/**
 *  @class
 *  @function ProductRecommendations
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }
  async fetchProducts() {
    try {
      const response = await fetch(this.dataset.url);
      const text = await response.text();
      
      const html = document.createElement('div');
      html.innerHTML = text;
      
      const recommendations = html.querySelector('product-recommendations');
      const arrowsContainer = html.querySelector(".cart-scroll-container-buttons");
  
      if (recommendations && recommendations.innerHTML.trim().length) {
        if (arrowsContainer) {
          arrowsContainer.classList.remove("hidden");
        }
        this.innerHTML = recommendations.innerHTML;
        document.dispatchEvent(new CustomEvent('product:recommendations-loaded', {
          detail: {
            container: this
          }
        }));
      }
  
      this.classList.add('product-recommendations--loaded');
    } catch (e) {
      console.error(e);
    }
  }
  
  connectedCallback() {
    this.fetchProducts();
  }
}
customElements.define('product-recommendations', ProductRecommendations);

function formatCartRecommendationMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents, theme.settings.money_with_currency_format);
  }
  return `${(cents / 100).toFixed(2)}`;
}

function getCartRecommendationSections() {
  return [{
    id: 'Cart',
    section: 'main-cart',
    selector: '.thb-cart-form'
  },
  {
    id: 'Cart-Drawer',
    section: 'cart-drawer',
    selector: '.cart-drawer'
  },
  {
    id: 'cart-drawer-toggle',
    section: 'cart-bubble',
    selector: '.thb-item-count'
  }];
}

function getCartRecommendationSectionInnerHTML(html, selector) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const element = parsed.querySelector(selector);
  return element ? element.innerHTML : '';
}

function setCartRecommendationStatus(card, message, type) {
  const status = card.querySelector('.js-cart-recommendation-status');
  if (!status) {
    return;
  }
  status.textContent = message || '';
  status.dataset.state = type || '';
}

function getCartRecommendationVariantFromSelect(card) {
  const select = card.querySelector('.js-cart-recommendation-select');
  const selectedOption = select ? select.selectedOptions[0] : null;

  if (selectedOption) {
    return {
      id: Number(selectedOption.value),
      available: !selectedOption.disabled,
      title: selectedOption.textContent.trim()
    };
  }

  const initialVariantId = Number(card.dataset.initialVariantId);
  return {
    id: initialVariantId,
    available: true,
    title: ''
  };
}

function syncCartRecommendationCard(card) {
  const variant = getCartRecommendationVariantFromSelect(card);
  const button = card.querySelector('.js-cart-recommendation-add');
  const price = card.querySelector('.js-cart-recommendation-price .price');
  const select = card.querySelector('.js-cart-recommendation-select');

  if (select && select.selectedOptions[0]) {
    const selectedOption = select.selectedOptions[0];
    const priceValue = Number(selectedOption.dataset.price);
    const compareAtPrice = Number(selectedOption.dataset.compareAtPrice);

    if (price) {
      if (compareAtPrice > priceValue) {
        price.innerHTML = `<del><span class="amount">${formatCartRecommendationMoney(compareAtPrice)}</span></del><ins><span class="amount">${formatCartRecommendationMoney(priceValue)}</span></ins>`;
      } else {
        price.innerHTML = `<ins><span class="amount">${formatCartRecommendationMoney(priceValue)}</span></ins>`;
      }
    }
  }

  if (!button) {
    return;
  }

  button.dataset.variantId = variant.id;

  if (variant.available) {
    button.disabled = false;
    button.querySelector('span').textContent = theme.variantStrings.addToCart;
    setCartRecommendationStatus(card, '', '');
  } else {
    button.disabled = true;
    button.querySelector('span').textContent = theme.variantStrings.soldOut;
    setCartRecommendationStatus(card, `${variant.title || theme.variantStrings.soldOut} unavailable`, 'error');
  }
}

function renderCartRecommendationResponse(parsedState) {
  getCartRecommendationSections().forEach((section) => {
    const sectionContainer = document.getElementById(section.id);
    if (!sectionContainer) {
      return;
    }

    const elementToReplace = sectionContainer.querySelector(section.selector) || sectionContainer;
    const replacementHTML = getCartRecommendationSectionInnerHTML(parsedState.sections[section.section], section.selector);

    if (replacementHTML) {
      elementToReplace.innerHTML = replacementHTML;
    }
  });

  const cartDrawer = document.getElementById('Cart-Drawer');
  if (cartDrawer) {
    document.body.classList.add('open-cc');
    document.body.style.overflow = 'hidden';
    setSidePanelInteractivity(cartDrawer, true);
    cartDrawer.classList.add('active');
  }
}

async function addCartRecommendationToCart(card) {
  const button = card.querySelector('.js-cart-recommendation-add');
  const variantId = button && button.dataset.variantId;

  if (!button || !variantId || button.disabled) {
    return;
  }

  button.disabled = true;
  button.classList.add('loading');
  setCartRecommendationStatus(card, '', '');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  getCartRecommendationSections().forEach((section) => {
    formData.append('sections', section.section);
  });
  formData.append('sections_url', window.location.pathname);

  try {
    const response = await fetch(`${theme.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    });
    const parsedResponse = await response.json();

    if (parsedResponse.status) {
      setCartRecommendationStatus(card, parsedResponse.description || parsedResponse.message || theme.variantStrings.unavailable, 'error');
      syncCartRecommendationCard(card);
      return;
    }

    renderCartRecommendationResponse(parsedResponse);
    document.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: {
        product: parsedResponse.hasOwnProperty('items') ? parsedResponse.items[0] : parsedResponse
      }
    }));
  } catch (error) {
    console.error(error);
    setCartRecommendationStatus(card, 'Unable to add this item right now.', 'error');
    syncCartRecommendationCard(card);
  } finally {
    button.classList.remove('loading');
    syncCartRecommendationCard(card);
  }
}

function initializeCartRecommendationCard(card) {
  if (card.dataset.recommendationBound === 'true') {
    syncCartRecommendationCard(card);
    return;
  }

  card.dataset.recommendationBound = 'true';
  const select = card.querySelector('.js-cart-recommendation-select');
  if (select) {
    select.addEventListener('change', () => {
      syncCartRecommendationCard(card);
    });
  }

  const button = card.querySelector('.js-cart-recommendation-add');
  if (button) {
    button.addEventListener('click', () => addCartRecommendationToCart(card));
  }

  syncCartRecommendationCard(card);
}

function initializeCartDrawerRecommendations(container) {
  const root = container || document;
  root.querySelectorAll('.js-cart-recommendation').forEach((card) => {
    initializeCartRecommendationCard(card);
  });
}


document.addEventListener("DOMContentLoaded", () => {
  initializeCartDrawerRecommendations();
  const isMobileView = window.innerWidth < 750;

  if (isMobileView) {
    const scrollingTextInner = document.querySelector('.scrolling-text_inner');
    const scrollingContainers = document.querySelectorAll('.scrolling-text-container');

    if (scrollingTextInner && scrollingContainers.length > 0) {
      // Function to toggle animation state
      const toggleAnimationState = (shouldPause) => {
        scrollingContainers.forEach(container => {
          container.classList.toggle('paused-animation', shouldPause);
        });
      };

      // Stop animation on touch hold
      scrollingTextInner.addEventListener('touchstart', (event) => {
        if (!event.target.closest('a')) {
          toggleAnimationState(true);
          event.preventDefault();
        }
      });

      // Resume animation on touch release or cancel
      ['touchend', 'touchcancel'].forEach(eventType => {
        scrollingTextInner.addEventListener(eventType, () => {
          toggleAnimationState(false);
        });
      });
    }
  }
});

document.addEventListener('product:recommendations-loaded', (event) => {
  const container = event.detail && event.detail.container ? event.detail.container : document;
  initializeCartDrawerRecommendations(container);
});